import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Generate hours array (00-23)
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString().padStart(2, "0"),
  label: i.toString().padStart(2, "0"),
}));

// Generate minutes array (00, 15, 30, 45)
const MINUTES_15 = ["00", "15", "30", "45"].map((m) => ({
  value: m,
  label: m,
}));

// Generate minutes array for 1-minute intervals (00-59)
const MINUTES_1 = Array.from({ length: 60 }, (_, i) => ({
  value: i.toString().padStart(2, "0"),
  label: i.toString().padStart(2, "0"),
}));

interface TimeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  minuteInterval?: 1 | 15 | 30 | 60;
  placeholder?: string;
}

export const TimeSelect: React.FC<TimeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
  minuteInterval = 15,
  // placeholder is defined in interface but not used in implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  placeholder = "Select time",
}) => {
  const [hour, minute] = value.split(":");

  const handleHourChange = (newHour: string) => {
    onChange(`${newHour}:${minute || "00"}`);
  };

  const handleMinuteChange = (newMinute: string) => {
    onChange(`${hour || "00"}:${newMinute}`);
  };

  // Generate minutes based on interval
  const getMinuteOptions = () => {
    switch (minuteInterval) {
      case 1:
        return MINUTES_1;
      case 30:
        return ["00", "30"].map((m) => ({ value: m, label: m }));
      case 60:
        return ["00"].map((m) => ({ value: m, label: m }));
      default:
        return MINUTES_15;
    }
  };

  const minuteOptions = getMinuteOptions();

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <Select value={hour} onValueChange={handleHourChange} disabled={disabled}>
        <SelectTrigger className="w-14 h-8 bg-background text-sm">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map((h) => (
            <SelectItem key={h.value} value={h.value}>
              {h.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground text-sm">:</span>

      <Select value={minute} onValueChange={handleMinuteChange} disabled={disabled}>
        <SelectTrigger className="w-14 h-8 bg-background text-sm">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent>
          {minuteOptions.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Utility function to validate time string format
export const isValidTimeString = (time: string): boolean => {
  const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(time);
};

// Utility function to format time string
export const formatTimeString = (hours: number, minutes: number): string => {
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

// Utility function to parse time string
export const parseTimeString = (time: string): { hours: number; minutes: number } | null => {
  if (!isValidTimeString(time)) return null;

  const [hours, minutes] = time.split(":").map(Number);
  return { hours, minutes };
};

// Utility function to calculate duration between two times
export const calculateDuration = (startTime: string, endTime: string): number => {
  const start = parseTimeString(startTime);
  const end = parseTimeString(endTime);

  if (!start || !end) return 0;

  const startMinutes = start.hours * 60 + start.minutes;
  const endMinutes = end.hours * 60 + end.minutes;

  return Math.max(0, endMinutes - startMinutes);
};

// Utility function to format duration in hours
export const formatDurationHours = (minutes: number): string => {
  const hours = minutes / 60;
  return hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
};
